import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Edit, LogIn, Plus, Search, ShieldOff, UserRound } from "lucide-react";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Textarea } from "../../components/ui/textarea";
import { useApiMessages } from "../../hooks/useApiMessages";
import { formatCedula, formatPhoneNumber } from "../../lib/utils";
import { useVisitorScope } from "../../components/visitors/useVisitorScope";
import { PageHeader, ScopeFilters, StatusBadge } from "../../components/visitors/VisitorShared";
import { CheckInDialog, VisitorFields } from "../../components/visitors/VisitorForms";
import { VisitDetailDialog } from "../../components/visitors/VisitDetailDialog";
import { fmtDateTime, fmtDuration, visitorApi } from "../../components/visitors/visitorUtils";

const EMPTY = {
  full_name: "",
  id_type: "national_id",
  id_number: "",
  visitor_company: "",
  phone: "",
  email: "",
  photo_base64: null,
  notes: "",
  is_frequent: false,
};

export default function VisitorRegistry() {
  const { t } = useTranslation();
  const { getErrorMessage } = useApiMessages();
  const scope = useVisitorScope();
  const [visitors, setVisitors] = useState([]);
  const [search, setSearch] = useState("");
  const [frequentOnly, setFrequentOnly] = useState(false);
  const [editing, setEditing] = useState(null); // null | "new" | visitor
  const [form, setForm] = useState(EMPTY);
  const [profile, setProfile] = useState(null);
  const [checkInVisitor, setCheckInVisitor] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(async () => {
    try {
      setVisitors(
        await visitorApi.get("/people", { company_id: scope.companyId, search, frequent_only: frequentOnly || undefined, limit: 300 })
      );
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope.companyId, search, frequentOnly]);

  useEffect(() => {
    const handle = setTimeout(load, 300);
    return () => clearTimeout(handle);
  }, [load]);

  const openProfile = async (v) => {
    try {
      setProfile(await visitorApi.get(`/people/${v.id}`));
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.loadError"));
    }
  };

  const openEdit = (v) => {
    setEditing(v || "new");
    setForm(
      v
        ? {
            ...EMPTY,
            ...v,
            phone: formatPhoneNumber(v.phone || ""),
            id_number: /^\d{11}$/.test(v.id_number || "") ? formatCedula(v.id_number) : v.id_number,
          }
        : EMPTY
    );
  };

  const save = async () => {
    if (!form.full_name.trim()) return toast.error(t("visitors.access.nameRequired"));
    const payload = {
      full_name: form.full_name,
      id_type: form.id_type,
      id_number: form.id_number || null,
      visitor_company: form.visitor_company || null,
      phone: form.phone || null,
      email: form.email || null,
      photo_base64: form.photo_base64 || null,
      notes: form.notes || null,
      is_frequent: !!form.is_frequent,
    };
    try {
      if (editing === "new") {
        await visitorApi.post("/people", { ...payload, company_id: scope.effectiveCompanyId });
      } else {
        if (scope.isManager) {
          payload.is_blocked = !!form.is_blocked;
          payload.block_reason = form.block_reason || "";
        }
        await visitorApi.put(`/people/${editing.id}`, payload);
      }
      toast.success(t("visitors.registry.saved"));
      setEditing(null);
      load();
      if (profile && editing !== "new" && profile.id === editing.id) openProfile(editing);
    } catch (error) {
      toast.error(getErrorMessage(error, "visitors.errors.saveError"));
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={t("visitors.registry.title")}
        subtitle={t("visitors.registry.subtitle")}
        actions={
          <Button onClick={() => openEdit(null)}>
            <Plus className="h-4 w-4 mr-2" />
            {t("visitors.registry.newVisitor")}
          </Button>
        }
      />

      <ScopeFilters scope={scope} showFacility={false}>
        <div className="lg:col-span-2">
          <Label className="text-sm text-slate-600 mb-1 block">{t("visitors.common.search")}</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder={t("visitors.access.searchVisitor")} value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        <label className="flex items-center gap-2 cursor-pointer pb-2">
          <Checkbox checked={frequentOnly} onCheckedChange={(c) => setFrequentOnly(!!c)} />
          <span className="text-sm">{t("visitors.registry.frequentOnly")}</span>
        </label>
      </ScopeFilters>

      <Card>
        <CardContent className="p-0 sm:p-2">
          {visitors.length === 0 ? (
            <p className="text-center py-10 text-slate-500">{t("visitors.common.noResults")}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("visitors.common.visitor")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("visitors.common.idNumber")}</TableHead>
                  <TableHead className="hidden md:table-cell">{t("visitors.common.visitorCompany")}</TableHead>
                  <TableHead className="hidden lg:table-cell">{t("visitors.registry.lastVisit")}</TableHead>
                  <TableHead className="hidden sm:table-cell">{t("visitors.registry.totalVisits")}</TableHead>
                  <TableHead className="text-right">{t("visitors.common.actions")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visitors.map((v) => (
                  <TableRow key={v.id} className="cursor-pointer" onClick={() => openProfile(v)}>
                    <TableCell>
                      <p className="font-medium">{v.full_name}</p>
                      <div className="flex gap-1 mt-0.5">
                        {v.is_frequent && <Badge variant="secondary">{t("visitors.common.frequent")}</Badge>}
                        {v.is_blocked && <Badge variant="destructive">{t("visitors.common.blocked")}</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="hidden md:table-cell">{v.id_number || "—"}</TableCell>
                    <TableCell className="hidden md:table-cell">{v.visitor_company || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell">{fmtDateTime(v.last_visit_at)}</TableCell>
                    <TableCell className="hidden sm:table-cell">{v.visit_count || 0}</TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openEdit(v)} title={t("visitors.registry.editVisitor")}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button size="sm" onClick={() => setCheckInVisitor(v)} disabled={v.is_blocked || scope.facilities.length === 0}>
                          <LogIn className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? t("visitors.registry.newVisitor") : t("visitors.registry.editVisitor")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <VisitorFields value={form} onChange={setForm} />
            <div>
              <Label className="mb-1 block">{t("visitors.common.notes")}</Label>
              <Textarea rows={2} value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox checked={!!form.is_frequent} onCheckedChange={(c) => setForm({ ...form, is_frequent: !!c })} />
              <span className="text-sm">{t("visitors.registry.frequentHelp")}</span>
            </label>
            {scope.isManager && editing !== "new" && (
              <div className="rounded-lg border border-red-200 p-3 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox checked={!!form.is_blocked} onCheckedChange={(c) => setForm({ ...form, is_blocked: !!c })} />
                  <ShieldOff className="h-4 w-4 text-red-600" />
                  <span className="text-sm font-medium">{t("visitors.registry.block")}</span>
                </label>
                {form.is_blocked && (
                  <Input
                    placeholder={t("visitors.registry.blockReason")}
                    value={form.block_reason || ""}
                    onChange={(e) => setForm({ ...form, block_reason: e.target.value })}
                  />
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              {t("visitors.common.cancel")}
            </Button>
            <Button onClick={save}>{t("visitors.common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!profile} onOpenChange={(o) => !o && setProfile(null)}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserRound className="h-5 w-5" />
              {profile?.full_name}
            </DialogTitle>
          </DialogHeader>
          {profile && (
            <div className="space-y-4">
              <div className="flex gap-4">
                {profile.photo_base64 && <img src={profile.photo_base64} alt="" className="h-24 w-24 rounded-lg object-cover border" />}
                <div className="text-sm space-y-1">
                  <p>{[profile.id_type && t(`visitors.idTypes.${profile.id_type}`), profile.id_number].filter(Boolean).join(": ")}</p>
                  <p>{[profile.visitor_company, profile.phone, profile.email].filter(Boolean).join(" · ")}</p>
                  {profile.vehicles?.length > 0 && (
                    <p>
                      {t("visitors.registry.vehicles")}: {profile.vehicles.join(", ")}
                    </p>
                  )}
                  {profile.notes && <p className="text-slate-600">{profile.notes}</p>}
                  {profile.is_blocked && (
                    <Badge variant="destructive">
                      {t("visitors.common.blocked")}
                      {profile.block_reason ? `: ${profile.block_reason}` : ""}
                    </Badge>
                  )}
                </div>
              </div>
              <h4 className="font-semibold">
                {t("visitors.registry.history")} ({profile.visits.length})
              </h4>
              {profile.visits.length === 0 ? (
                <p className="text-sm text-slate-500">{t("visitors.history.noVisits")}</p>
              ) : (
                <div className="border rounded-lg divide-y">
                  {profile.visits.map((v) => (
                    <button
                      key={v.id}
                      onClick={() => setDetailId(v.id)}
                      className="w-full text-left px-3 py-2 flex items-center justify-between gap-2 hover:bg-slate-50 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          {v.facility_name} {v.host_name ? `· ${v.host_name}` : ""}
                        </p>
                        <p className="text-xs text-slate-500">
                          {fmtDateTime(v.check_in_at || v.created_at)} · {fmtDuration(v.duration_minutes)}
                        </p>
                      </div>
                      <StatusBadge status={v.status} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => openEdit(profile)}>
              <Edit className="h-4 w-4 mr-2" />
              {t("visitors.registry.editVisitor")}
            </Button>
            <Button onClick={() => setCheckInVisitor(profile)} disabled={profile?.is_blocked || scope.facilities.length === 0}>
              <LogIn className="h-4 w-4 mr-2" />
              {t("visitors.access.registerEntry")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {checkInVisitor && (
        <CheckInDialog
          open={!!checkInVisitor}
          onOpenChange={(o) => !o && setCheckInVisitor(null)}
          scope={scope}
          preset={{ visitor: checkInVisitor }}
          onDone={() => {
            load();
            if (profile) openProfile(profile);
          }}
        />
      )}
      <VisitDetailDialog visitId={detailId} open={!!detailId} onOpenChange={(o) => !o && setDetailId(null)} scope={scope} />
    </div>
  );
}
